<script>
import moment from "moment";
import axios from '@/utils/axios';
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";

export default {
  inject: ['setLoading'],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "Inventory",
      text1: "Stock Out Report",
      app_currency: '',
      filter: {
        from_date: '',
        to_date: '',
        warehouse_id: '',
        item_id: '',
        recipient_type: '',
        search: '',
      },
      summary: {},
      data: [],
      warehouses: [],
      items: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    // Recipient-wise breakdown from current page data
    recipientBreakdown() {
      const typeMap = {
        'App\\Models\\Engineer': { label: 'Engineer', icon: 'ti-user', color: 'purple' },
        'App\\Models\\Customer': { label: 'Customer', icon: 'ti-user-shield', color: 'info' },
        'App\\Models\\WorkOrder': { label: 'Work Order', icon: 'ti-clipboard-list', color: 'success' },
        'App\\Models\\SalesExecutive': { label: 'Sales Executive', icon: 'ti-briefcase', color: 'warning' },
      };
      const breakdown = {};
      (this.data || []).forEach(row => {
        const type = row.recipient_type || 'other';
        if (!breakdown[type]) {
          const info = typeMap[type] || { label: 'Other', icon: 'ti-point', color: 'secondary' };
          breakdown[type] = { ...info, count: 0, qty: 0, amount: 0 };
        }
        breakdown[type].count++;
        breakdown[type].qty += parseFloat(row.qty || 0);
        breakdown[type].amount += parseFloat(row.taxable_amount || 0);
      });
      return Object.values(breakdown).filter(b => b.count > 0);
    },

    itemBarChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.item?.name || 'Unknown';
        const qty = parseFloat(row.qty || 0);
        map[name] = (map[name] || 0) + qty;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#ef4444', '#f59e0b', '#4f46e5', '#06b6d4', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6'];
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Qty Dispatched',
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderRadius: 6,
            barThickness: 40,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },

    warehouseDoughnutChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.warehouse?.name || 'Unknown';
        const amt = parseFloat(row.taxable_amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },

    recipientDoughnutChart() {
      const typeMap = {
        'App\\Models\\Engineer': 'Engineer',
        'App\\Models\\Customer': 'Customer',
        'App\\Models\\WorkOrder': 'Work Order',
        'App\\Models\\SalesExecutive': 'Sales Executive',
      };
      const map = {};
      (this.data || []).forEach(row => {
        const name = typeMap[row.recipient_type] || 'Other';
        const amt = parseFloat(row.taxable_amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#8b5cf6', '#06b6d4', '#10b981', '#ef4444', '#f59e0b'];
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },
  },

  async mounted() {
    this.app_currency = await this.$appCurrency();
    this.getDropdowns();

    const start = moment().subtract(6, "days");
    const end = moment();

    new DateRangePicker(
    this.$refs.dateRangeInput,
    {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: { 
          format: "DD/MM/YYYY", 
          separator: " - ",
          cancelLabel: "Clear"
        },
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        }
    },
    (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
    }
    );

    this.filterReport();
  },

  methods: {

    recipientType(row) {
    const map = {
        'App\\Models\\Engineer': { label: 'Engineer', class: 'bg-purple-subtle text-purple' },
        'App\\Models\\Customer': { label: 'Customer', class: 'bg-info-subtle text-info' },
        'App\\Models\\WorkOrder': { label: 'Work Order', class: 'bg-success-subtle text-success' },
        'App\\Models\\SalesExecutive': { label: 'Sales Executive', class: 'bg-warning-subtle text-warning' },
    };
    return map[row.recipient_type] || { label: '-', class: 'bg-secondary-subtle text-secondary' };
},

    recipientTypeLabel(row) {
      const map = {
        'App\\Models\\Engineer': 'Engineer',
        'App\\Models\\Customer': 'Customer',
        'App\\Models\\WorkOrder': 'Work Order',
      };
      return map[row.recipient_type] || '-';
    },

    dispatchInfo(row) {
      const dc = row.delivery_challan;
      if (!dc) return null;
      const parts = [];
      if (dc.vehicle_no) parts.push(dc.vehicle_no);
      if (dc.transporter_name) parts.push(dc.transporter_name);
      return parts.length ? parts.join(' · ') : null;
    },

    openChallan(id) {
      window.open(`/inventory/delivery-challans/view/${id}`, '_blank');
    },

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        if (this.filter.recipient_type) payload.recipient_type = this.filter.recipient_type;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/stock-out?${query_string}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stock-out-report-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = {
          page,
          date_from: this.filter.from_date,
          date_to: this.filter.to_date,
        };
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        if (this.filter.recipient_type) payload.recipient_type = this.filter.recipient_type;
        if (this.filter.search) payload.search = this.filter.search.trim();

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/stock-out?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const [warehouseRes, itemRes] = await Promise.all([
          axios.get('/inventory/warehouses/dropdown'),
          axios.get('/items/dropdown'),
        ]);
        this.warehouses = warehouseRes.data.data;
        this.items = itemRes.data.data;
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.warehouse_id || this.filter.item_id || this.filter.recipient_type || this.filter.from_date || this.filter.to_date || this.filter.search;
    },

    clearFilter() {
      this.filter.warehouse_id = '';
      this.filter.item_id = '';
      this.filter.recipient_type = '';
      this.filter.from_date = '';
      this.filter.to_date = '';
      this.filter.search = '';
      this.$refs.dateRangeInput.value = '';
      this.filterReport();
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        + ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
    },

    formatQty(val) {
      const num = parseFloat(val || 0);
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    },

    changePctClass(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'text-danger';
      if (num < 0) return 'text-success';
      return 'text-muted';
    },

    changePctIcon(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'ti ti-trending-up';
      if (num < 0) return 'ti ti-trending-down';
      return 'ti ti-minus';
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_dispatches">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Dispatches</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_dispatches }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Qty</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatQty(summary.total_qty) }}</span>
              <span v-if="summary.qty_change_pct != null" :class="changePctClass(summary.qty_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.qty_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.qty_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Value</span>
              <span class="fw-bold text-danger" style="font-size:20px;">{{ formatCurrency(summary.total_value) }}</span>
              <span v-if="summary.value_change_pct != null" :class="changePctClass(summary.value_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.value_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.value_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Tax</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.total_tax) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unique Items</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.unique_items }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Dispatch Value</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.avg_dispatch_value) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Recipient-wise Breakdown Cards -->
      <div class="row g-3 mb-3" v-if="recipientBreakdown.length > 1">
        <div class="col" v-for="src in recipientBreakdown" :key="src.label">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="d-flex align-items-center">
                  <i class="ti me-2" :class="[src.icon, 'text-' + src.color]" style="font-size:18px;"></i>
                  <span class="fw-semibold" style="font-size:13px;">{{ src.label }}</span>
                </span>
                <span class="badge rounded-pill" :class="'bg-' + src.color + '-subtle text-' + src.color">{{ src.count }}</span>
              </div>
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">{{ formatQty(src.qty) }} qty</small>
                <small class="fw-semibold">{{ formatCurrency(src.amount) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row g-3 mb-3" v-if="data && data.length">
        <div class="col-md-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-chart-bar me-1 text-danger"></i>Item-wise Qty Dispatched</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="itemBarChart" :key="'bar-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-building-warehouse me-1 text-primary"></i>By Warehouse</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="warehouseDoughnutChart" :key="'wh-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-users me-1 text-purple"></i>By Recipient</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="recipientDoughnutChart" :key="'rc-'+data.length" />
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput" placeholder="dd/mm/yyyy - dd/mm/yyyy">
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.warehouse_id"
                :options="warehouses"
                label="name"
                :reduce="w => w.id"
                placeholder="Warehouse"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.item_id"
                :options="items"
                label="name"
                :reduce="i => i.id"
                placeholder="Item"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.recipient_type"
                :options="[
                  { label: 'Engineer', value: 'App\\Models\\Engineer' },
                  { label: 'Customer', value: 'App\\Models\\Customer' },
                  { label: 'Work Order', value: 'App\\Models\\WorkOrder' },
                  { label: 'Sales Executive', value: 'App\\Models\\SalesExecutive' },
                ]"
                label="label"
                :reduce="r => r.value"
                placeholder="Recipient Type"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Item</th>
                    <th>Warehouse</th>
                    <th>Challan</th>
                    <th>Recipient</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Tax</th>
                    <th>Amount</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>
                      <span>{{ formatDate(row.created_at) }}</span>
                      <small class="text-muted d-block" style="font-size:10px;">{{ formatDateTime(row.created_at).split(', ')[1] }}</small>
                    </td>
                    <td>
                      <strong class="text-dark">{{ row.item?.name }}</strong>
                      <small class="text-muted d-block" style="font-size:11px;">{{ row.item?.code }}</small>
                    </td>
                    <td>
                      <span class="badge bg-light text-primary border rounded-pill">{{ row.warehouse?.name || '-' }}</span>
                    </td>
                    <td>
                      <a v-if="row.delivery_challan" href="javascript:void(0);" class="text-primary" @click="openChallan(row.delivery_challan.id)">
                        {{ row.delivery_challan.challan_number }}
                      </a>
                      <span v-else class="text-muted">-</span>
                      <small v-if="dispatchInfo(row)" class="text-muted d-block" style="font-size:10px;">
                        <i class="ti ti-truck me-1"></i>{{ dispatchInfo(row) }}
                      </small>
                    </td>
                    <td>
                      <span class="badge rounded-pill" :class="recipientType(row).class">
                        {{ recipientType(row).label }}
                      </span>
                    </td>
                    <td><strong>{{ formatQty(row.qty) }}</strong></td>
                    <td>{{ formatCurrency(row.rate) }}</td>
                    <td>
                      <span v-if="row.tax">{{ row.tax.name }}</span>
                      <span v-else class="text-muted">-</span>
                      <small v-if="row.tax" class="text-muted d-block" style="font-size:10px;">{{ formatCurrency(row.tax_amount) }}</small>
                    </td>
                    <td><strong>{{ formatCurrency(row.taxable_amount) }}</strong></td>
                    <td>
                      <small class="text-muted">{{ row.created_by?.name || '-' }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.table-responsive {
  overflow-y: auto;
}
</style>